import { useMolecule } from 'bunshi/react'
import React from 'react'
import {
  FormProvider,
  type SubmitHandler,
  useFieldArray,
  useForm,
} from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { ExecuteCancelDialog } from '~/components/elements/ExecuteCancelDialog'
import {
  StyledButton,
  type StyledButtonProps,
} from '~/components/elements/StyledButton'
import { Typography } from '~/components/elements/Typography'
import { VStack } from '~/components/elements/VStack'
import { useTurnOffAtom } from '~/hooks/jotai'

import { diffsContext, onExecuteContext } from '../../contexts'
import { isVisibleDialogMolecule } from '../../molecules'
import { type InputValues, resolver } from '../../utils'

import { DiffItemSelectors } from './DiffItemSelectors'
import styles from './EditDiffDialog.css'

export const EditDiffDialog = () => {
  const { t } = useTranslation()
  const diffs = React.useContext(diffsContext)
  const methods = useForm<InputValues>({
    defaultValues: { diffs: 0 < diffs.length ? diffs : [[null, null]] },
    mode: 'onChange',
    resolver,
  })
  const {
    control,
    formState: { isValid },
    handleSubmit,
  } = methods
  const { append, fields, remove } = useFieldArray({ control, name: 'diffs' })

  const isVisibleDialogAtom = useMolecule(isVisibleDialogMolecule)
  const closeDialog = useTurnOffAtom(isVisibleDialogAtom)

  const onExecute = React.useContext(onExecuteContext)
  const handleValid = React.useCallback<SubmitHandler<InputValues>>(
    ({ diffs }) => {
      onExecute?.(diffs)
      closeDialog()
    },
    [closeDialog, onExecute],
  )

  const handleAddButtonClick = React.useCallback<
    EH<StyledButtonProps, 'onClick'>
  >(() => {
    append([[null, null]])
  }, [append])

  return (
    <FormProvider {...methods}>
      <ExecuteCancelDialog
        disabledExecute={!isValid}
        executeButtonText={t('save_changes')}
        mode="settings"
        title={t('edit_comparison_columns_1')}
        onCancel={closeDialog}
        onExecute={handleSubmit(handleValid)}
      >
        <VStack spacing={4}>
          <Typography variant="normal">
            {t('can_calculate_left_right')}
          </Typography>

          {0 < fields.length && (
            <VStack spacing={2}>
              {fields.map((field, index) => (
                <DiffItemSelectors
                  key={field.id}
                  index={index}
                  onDelete={remove}
                />
              ))}
            </VStack>
          )}

          <StyledButton
            className={styles.addButton}
            fillMode="flat"
            onClick={handleAddButtonClick}
          >
            {t('add_comparison_column')}
          </StyledButton>
        </VStack>
      </ExecuteCancelDialog>
    </FormProvider>
  )
}
